import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Paperclip, Video, FileUp, CheckCircle2, RotateCcw,
  Download, X, Wifi, WifiOff, Play, ExternalLink, Link as LinkIcon, Clock,
  ChevronRight, MessageCircle, FolderOpen, Phone, Users, Activity, Trash2,
  FileText, Palette, Eraser, Save, Plus, Bold, Italic, Code, List,
  ListChecks, Quote, Heading1, Heading2, Maximize2, Minimize2,
  MousePointer2, Pen, Minus as MinusIcon, MoveUpRight, Square, Circle, Type,
  Github, Info,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ParticipantsCard from "@/components/ParticipantsCard";
import ProjectChat from "@/components/ProjectChat";
import ActiveCallBanner from "@/components/ActiveCallBanner";
import DrivePreviewSidebar from "@/components/DrivePreviewSidebar";
import SlotNegotiator from "@/components/SlotNegotiator";
import IdeaLinksPanel from "@/components/IdeaLinksPanel";
import { MeetingsBar, KanbanBoard, DriveTab } from "@/components/WorkspaceHub";
import { WhiteboardTab as ExcalidrawWhiteboardTab } from "@/components/WhiteboardExcalidraw";
import { requestExtraScopes } from "@/lib/googleAuth";
import { scrollWithinContainer } from "@/lib/scroll";
import { useCall } from "@/contexts/CallContext";

const SCOPE_MEET = "openid email profile https://www.googleapis.com/auth/meetings.space.created";
const SCOPE_DRIVE = "openid email profile https://www.googleapis.com/auth/drive.file";

const TYPE_LABELS = {
  team: "Team workspace",
  topic_selection: "Topic selection",
  literature_review: "Literature review",
  methodology_design: "Methodology design",
  methodology_refinement: "Methodology refinement",
  methodology_implementation: "Methodology implementation",
  statistical_analysis: "Statistical analysis",
  technical_implementation: "Technical implementation",
  publication_preparation: "Publication preparation",
};

const STATUS_BADGE = {
  submitted: { label: "Awaiting price", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  priced: { label: "Ready to pay", cls: "border-sky-200 bg-sky-50 text-sky-700" },
  paid: { label: "Paid · pending mentor", cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  assigned: { label: "Mentor assigned", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  in_progress: { label: "In progress", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  completed: { label: "Completed", cls: "border-emerald-300 bg-emerald-100 text-emerald-700" },
};

function formatBytes(kb) {
  if (!kb) return "—";
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// Clone of IdeaDetail with Paper Draft tab removed. Three tabs: Chat / Tasks / Drive.
// Same right-sidebar structure (Participants, Drive preview), with two
// guidance-specific cards (Deliverables, Shared Files, Legacy Meet) appended.
export default function TaskWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const [guidance, setGuidance] = useState(null);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [typing, setTyping] = useState(null);
  const [draftMsg, setDraftMsg] = useState("");
  const [deliverableForm, setDeliverableForm] = useState({ title: "", description: "", file_ids: [] });
  const [meetEditing, setMeetEditing] = useState(false);
  const [meetForm, setMeetForm] = useState({ meet_url: "", scheduled_at: "", note: "" });
  const [participants, setParticipants] = useState([]);
  const [googleStatus, setGoogleStatus] = useState({ has_drive: false, has_meet: false });
  const [activeTab, setActiveTab] = useState("chat");
  // Per-tab last-opened timestamp (localStorage) + latest activity ISO
  // (server). Tab shows a red dot when latest > lastVisit.
  const [tabActivity, setTabActivity] = useState({}); // {chat: iso, tasks: iso, …}
  const [tabVisits, setTabVisits] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`kb.ws.${id}.visits`) || "{}") || {}; }
    catch { return {}; }
  });
  const refreshTabActivity = React.useCallback(() => {
    api.get(`/workspace/${id}/tab-updates`).then((r) => setTabActivity(r.data || {})).catch(() => {});
  }, [id]);
  useEffect(() => {
    refreshTabActivity();
    const t = setInterval(refreshTabActivity, 60_000);
    return () => clearInterval(t);
  }, [refreshTabActivity]);
  const openTab = (k) => {
    setActiveTab(k);
    setTabVisits((prev) => {
      const next = { ...prev, [k]: new Date().toISOString() };
      try { localStorage.setItem(`kb.ws.${id}.visits`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const tabHasUpdate = (k) => {
    const srv = tabActivity[k];
    if (!srv) return false;
    const vis = tabVisits[k];
    return !vis || new Date(srv) > new Date(vis);
  };
  const [activeMeeting, setActiveMeeting] = useState(null);
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const deliverableFileRef = useRef(null);
  const { startCall, endCall } = useCall();

  // Mirror activeMeeting into the global call context so the panel survives
  // route changes. We intentionally DO NOT clear on unmount — the point of
  // this integration is that the call keeps running as you navigate.
  useEffect(() => {
    if (!activeMeeting) return;
    if (activeMeeting.provider === "google_meet") {
      startCall({ provider: "google_meet", meetingUri: activeMeeting.meeting_uri });
    } else {
      startCall({
        provider: "daily",
        roomUrl: activeMeeting.room_url,
        displayName: user?.name || user?.email || "Guest",
        audioOnly: !!activeMeeting.audio_only,
      });
    }
  }, [activeMeeting, startCall, user]);

  // ----- Initial load -----
  const reloadGuidance = React.useCallback(async () => {
    try { const g = await api.get(`/guidance/${id}`); setGuidance(g.data); } catch { /* ignore */ }
  }, [id]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [g, m, f, d] = await Promise.all([
          api.get(`/guidance/${id}`),
          api.get(`/guidance/${id}/messages`),
          api.get(`/guidance/${id}/files`),
          api.get(`/guidance/${id}/deliverables`),
        ]);
        if (!mounted) return;
        setGuidance(g.data);
        setMessages(m.data);
        setFiles(f.data);
        setDeliverables(d.data);
        setMeetForm({
          meet_url: g.data.meet_url || "",
          scheduled_at: g.data.meet_scheduled_at || "",
          note: g.data.meet_note || "",
        });
      } catch {
        toast.error("Could not load workspace");
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // ----- Participants + Google status, once guidance is open -----
  useEffect(() => {
    if (!guidance || !["assigned", "in_progress", "completed"].includes(guidance.status)) return;
    api.get(`/workspace/${guidance.id}/participants`).then((r) => setParticipants(r.data || [])).catch(() => {});
    api.get(`/auth/google/status`).then((r) => setGoogleStatus(r.data || {})).catch(() => {});
  }, [guidance?.id, guidance?.status]);

  // ----- WebSocket for chat / files / deliverables / meet updates -----
  useEffect(() => {
    if (!guidance || guidance.status === "submitted" || guidance.status === "priced") return;
    const token = localStorage.getItem("kb_session_token");
    if (!token) return;
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const apiHost = (BACKEND_URL || window.location.origin).replace(/^https?:/, "");
    const wsUrl = `${wsProtocol}${apiHost}/api/ws/guidance/${id}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload.type === "message") setMessages((m) => [...m.filter((x) => x.id !== payload.data.id), payload.data]);
        else if (payload.type === "message_deleted") setMessages((m) => m.filter((x) => x.id !== payload.data.id));
        else if (payload.type === "file") setFiles((f) => [payload.data, ...f.filter((x) => x.id !== payload.data.id)]);
        else if (payload.type === "file_deleted") setFiles((f) => f.filter((x) => x.id !== payload.data.id));
        else if (payload.type === "deliverable") setDeliverables((d) => [payload.data, ...d.filter((x) => x.id !== payload.data.id)]);
        else if (payload.type === "deliverable_review") setDeliverables((d) => d.map((x) => x.id === payload.data.id ? payload.data : x));
        else if (payload.type === "meet") setGuidance((g) => g ? { ...g, ...payload.data } : g);
        else if (payload.type === "typing" && payload.data.user_id !== user?.user_id) {
          setTyping(payload.data.name);
          setTimeout(() => setTyping(null), 2000);
        }
      } catch { /* ignore */ }
    };
    const pingInterval = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" })); }, 30000);
    return () => { clearInterval(pingInterval); ws.close(); wsRef.current = null; };
  }, [guidance?.id, guidance?.status, id, user?.user_id]);

  // ----- Auto-scroll chat -----
  useEffect(() => {
    scrollWithinContainer(chatEndRef.current);
  }, [messages.length]);

  // ----- Chat / file helpers (unchanged from previous implementation) -----
  const sendMessage = (e) => {
    e.preventDefault();
    if (!draftMsg.trim()) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "message", body: draftMsg.trim() }));
    else api.post(`/guidance/${id}/messages`, { body: draftMsg.trim() }).catch(() => toast.error("Send failed"));
    setDraftMsg("");
  };
  const onTyping = (e) => {
    setDraftMsg(e.target.value);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "typing" }));
  };

  // Paste-to-upload: if the clipboard has an image, silently upload it via
  // /guidance/{id}/files (as an "image" kind) and post a chat message with
  // the download link so peers can click through.
  const onPasteChat = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type?.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (!file) continue;
        if (file.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); continue; }
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64 = String(reader.result).split(",")[1] || "";
            const r = await api.post(`/guidance/${id}/files`, {
              name: `pasted-${Date.now()}.png`, mime: file.type,
              size_kb: Math.round(file.size / 1024), content_base64: base64, kind: "image",
            });
            const body = `📷 [image: ${r.data.name}](file:${r.data.id})`;
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN)
              ws.send(JSON.stringify({ type: "message", body }));
            else
              await api.post(`/guidance/${id}/messages`, { body });
            toast.success("Image sent");
          } catch (err) { toast.error(err?.response?.data?.detail || "Paste upload failed"); }
        };
        reader.readAsDataURL(file);
        return;
      }
    }
  };

  const deleteChatMessage = async (m) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await api.delete(`/guidance/${id}/messages/${m.id}`);
      // Optimistic — the WS broadcast will also drop it for us.
      setMessages((arr) => arr.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const copyChatMessage = async (m) => {
    try { await navigator.clipboard.writeText(m.body || ""); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  const uploadFile = async (e, target = "file") => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("File too large; max 4MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = String(reader.result).split(",")[1] || "";
      try {
        const r = await api.post(`/guidance/${id}/files`, {
          name: f.name, mime: f.type, size_kb: Math.round(f.size / 1024),
          content_base64: base64, kind: target,
        });
        toast.success(`Uploaded ${f.name}`);
        if (target === "deliverable_attach") setDeliverableForm((d) => ({ ...d, file_ids: [...d.file_ids, r.data.id] }));
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(f);
    if (e.target) e.target.value = "";
  };
  const downloadFile = async (file_id, name) => {
    try {
      const r = await api.get(`/guidance/${id}/files/${file_id}`);
      const link = document.createElement("a");
      link.href = `data:${r.data.mime || "application/octet-stream"};base64,${r.data.content_base64}`;
      link.download = name;
      link.click();
    } catch { toast.error("Download failed"); }
  };
  const deleteFile = async (file_id) => {
    if (!window.confirm("Delete file?")) return;
    try { await api.delete(`/guidance/${id}/files/${file_id}`); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };
  const submitDeliverable = async (e) => {
    e.preventDefault();
    if (!deliverableForm.title.trim()) { toast.error("Title required"); return; }
    try {
      const r = await api.post(`/guidance/${id}/deliverables`, deliverableForm);
      toast.success("Deliverable submitted");
      // Optimistically prepend the new deliverable so the UI flips to
      // "SUBMITTED" without waiting for the ws broadcast round-trip.
      if (r?.data?.id) setDeliverables((d) => [r.data, ...d.filter((x) => x.id !== r.data.id)]);
      setDeliverableForm({ title: "", description: "", file_ids: [] });
    } catch { toast.error("Submit failed"); }
  };
  const reviewDeliverable = async (did, decision) => {
    const note = window.prompt(decision === "approved" ? "Approval note (optional)" : "Revision note (what needs change?)") || "";
    try {
      const r = await api.post(`/guidance/${id}/deliverables/${did}/review`, { decision, note });
      toast.success(decision === "approved" ? "Approved" : "Revision requested");
      if (r?.data?.id) setDeliverables((d) => d.map((x) => x.id === r.data.id ? r.data : x));
    }
    catch { toast.error("Failed"); }
  };
  const saveMeet = async () => {
    if (!meetForm.meet_url) { toast.error("Enter Google Meet URL"); return; }
    try { await api.put(`/guidance/${id}/meet`, meetForm); toast.success("Meeting saved"); setMeetEditing(false); }
    catch { toast.error("Failed"); }
  };

  // ----- Call helpers — cloned 1:1 from IdeaDetail -----
  const startInstantMeet = async (audioOnly = false) => {
    try {
      const r = await api.post(`/workspace/${guidance.id}/call/daily-room?audio_only=${audioOnly}`);
      const room = r.data;
      setActiveMeeting({ id: room.room_name, room_name: room.room_name, room_url: room.url, audio_only: audioOnly, provider: "daily" });
      try {
        await api.post(`/workspace/${guidance.id}/call/start`, { provider: "daily", room_name: room.room_name, join_url: room.url, audio_only: audioOnly });
        toast.success(`${audioOnly ? "Audio" : "Video"} call started · everyone in this workspace is being notified`);
      } catch {
        toast.success(`${audioOnly ? "Audio" : "Video"} call started (offline broadcast — other participants may need to refresh)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Daily.co call");
    }
  };
  const joinActiveCall = async (callDoc) => {
    if (callDoc.provider === "daily" || (!callDoc.provider && callDoc.join_url?.includes(".daily.co"))) {
      let url = callDoc.join_url;
      if (!url) {
        try {
          const r = await api.post(`/workspace/${guidance.id}/call/daily-room?audio_only=${!!callDoc.audio_only}`);
          url = r.data.url;
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't join the call"); return; }
      }
      setActiveMeeting({ id: callDoc.room_name, room_name: callDoc.room_name, room_url: url, audio_only: !!callDoc.audio_only, provider: "daily" });
      return;
    }
    setActiveMeeting({ id: callDoc.room_name, room_name: callDoc.room_name, audio_only: !!callDoc.audio_only, provider: callDoc.provider || "google_meet", meeting_uri: callDoc.join_url });
  };
  const closeCallPanel = async () => {
    setActiveMeeting(null);
    endCall();
    try { await api.post(`/workspace/${guidance.id}/call/end`); } catch { /* ignore */ }
  };

  // ----- Derived flags -----
  const role = user?.role;
  const isMentor = role === "mentor" || role === "tutor" || role === "admin";
  const isClient = guidance?.user_id === user?.user_id;
  const status = guidance?.status;
  const workspaceOpen = ["assigned", "in_progress", "completed"].includes(status);
  const meetEmbed = useMemo(() => guidance?.meet_url || null, [guidance?.meet_url]);
  const fileNameById = useMemo(() => Object.fromEntries(files.map((f) => [f.id, f])), [files]);
  const mentorObj = guidance?.mentor_user_id ? { user_id: guidance.mentor_user_id, name: guidance.mentor_name, role: "mentor" } : null;

  if (!guidance) return <div className="kb-mono text-xs text-kb-ink-muted">Loading workspace…</div>;

  // ============================== RENDER ==============================
  const isTeam = guidance.type === "team";
  const isTask = guidance.type === "employee_task";
  // Team & task workspaces intentionally start with every right-side card
  // COLLAPSED — they're operational spaces with many artefacts, so a clean
  // header stack keeps the entry-view fast to scan.
  const rightOpen = !(isTeam || isTask);
  const participantsHeader = isTeam
    ? `TEAM LEAD & MEMBERS · ${participants.length}`
    : isTask
      ? `ASSIGNER & COLLABORATORS · ${participants.length}`
      : `MENTOR & COLLABORATORS · ${participants.length}`;
  const participantsContext = isTeam ? "team" : isTask ? "employee_task" : undefined;
  return (
    <div data-testid="guidance-workspace" className={isTeam ? "kb-team-workspace" : ""}>
      <Link
        to={isTeam ? "/dashboard/teams" : "/dashboard/guidance"}
        className={`kb-mono text-xs inline-flex items-center gap-2 ${isTeam ? "text-teal-700 hover:text-teal-900" : "text-kb-ink-muted hover:text-kb-primary"}`}
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {isTeam ? "BACK TO TEAMS" : "BACK TO GUIDANCE"}
      </Link>

      {isTeam && (
        <div className="mt-4 rounded-sm border border-teal-200 bg-gradient-to-r from-teal-50 via-teal-50/60 to-transparent px-5 py-3 flex items-center gap-3" data-testid="team-workspace-banner">
          <div className="w-9 h-9 rounded-sm bg-teal-600 text-white flex items-center justify-center shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-teal-700">TEAM WORKSPACE</div>
            <div className="text-sm font-medium text-teal-900 truncate">{guidance.team_name || "Team"}</div>
          </div>
        </div>
      )}

      {/* TITLE CARD (collapsible) */}
      <details className="kb-card mt-4" data-testid="guidance-title-card">
        <summary className="cursor-pointer list-none px-7 py-5 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted truncate">
              {guidance.id} · {TYPE_LABELS[guidance.type] || guidance.type}
            </div>
            <h1 className="font-serif text-2xl text-kb-ink mt-1 leading-tight truncate">
              {(guidance.task_title || guidance.problem_description || "Task workspace").slice(0, 90)}
              {(guidance.task_title || guidance.problem_description || "").length > 90 ? "…" : ""}
            </h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isTeam && (
              <span className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-kb-primary/10 border border-kb-primary/30 text-kb-primary" data-testid="task-workspace-pill">
                Task Workspace
              </span>
            )}
            <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${STATUS_BADGE[status]?.cls || "border-slate-200"}`}>
              {STATUS_BADGE[status]?.label || status}
            </span>
            {workspaceOpen && (
              <span className={`kb-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1 ${wsConnected ? "text-emerald-700" : "text-rose-700"}`} data-testid="ws-status">
                {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {wsConnected ? "live" : "reconnecting"}
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
          </div>
        </summary>
        <div className="px-7 pb-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TASK DESCRIPTION</div>
          <div className="mt-3 kb-task-md leading-relaxed" data-testid="task-description-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{guidance.details || guidance.problem_description || "_No description provided._"}</ReactMarkdown>
          </div>
        </div>
      </details>

      {/* SLOT NEGOTIATION — active whenever the request is pre-payment */}
      {["awaiting_admin", "quoted", "scheduling"].includes(guidance.status) && (
        <div className="mt-4">
          <SlotNegotiator
            guidance={guidance}
            viewerRole={
              user?.user_id === guidance.user_id ? "client"
                : (user?.user_id === guidance.mentor_user_id || user?.linked_mentor_id === guidance.mentor_id ? "mentor"
                  : "other")
            }
            onChanged={reloadGuidance}
          />
        </div>
      )}

      {!workspaceOpen && (
        <div className="mt-6 border border-dashed border-slate-300 bg-white rounded-sm p-8 text-center" data-testid="workspace-locked">
          <FolderOpen className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-kb-ink-muted">
            {status === "submitted" && "Workspace unlocks once admin prices and you pay."}
            {status === "priced" && "Workspace unlocks after payment is completed."}
            {status === "paid" && "Workspace unlocks once admin assigns a mentor."}
          </p>
        </div>
      )}

      {workspaceOpen && (
        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          {/* ================== MAIN COLUMN ================== */}
          <div className="lg:col-span-9 space-y-5">
            <MeetingsBar workspaceId={guidance.id} status={googleStatus} onConnectMeet={() => requestExtraScopes(SCOPE_MEET)} />
            <ActiveCallBanner workspaceId={guidance.id} currentUserId={user?.user_id} onJoin={joinActiveCall} />

            {/* TAB BAR — Chat / Tasks / Drive (Paper Draft removed) */}
            <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-slate-200" data-testid="guidance-tabs">
              {[
                { key: "chat", label: "Project Chat", icon: MessageCircle },
                { key: "tasks", label: "Sub Tasks", icon: ChevronRight },
                { key: "progress", label: "Progress Log", icon: Activity },
                { key: "docs", label: "Docs", icon: FileText },
                { key: "whiteboard", label: "Whiteboard", icon: Palette },
                { key: "drive", label: "Drive Files", icon: FolderOpen },
              ].map((t) => {
                const Icon = t.icon;
                const active = activeTab === t.key;
                const hasUpdate = tabHasUpdate(t.key);
                return (
                  <button key={t.key} onClick={() => openTab(t.key)}
                    className={`relative flex items-center gap-2 px-4 py-2.5 text-sm kb-mono tracking-wider uppercase text-[11px] whitespace-nowrap border-b-2 transition-colors ${
                      active ? "border-kb-primary text-kb-primary bg-kb-primary/5" : "border-transparent text-kb-ink-muted hover:text-kb-ink hover:bg-slate-50"
                    }`}
                    data-testid={`guidance-tab-${t.key}`}>
                    <Icon className="w-4 h-4" /> {t.label}
                    {hasUpdate && !active && (
                      <span className="w-1.5 h-1.5 rounded-full bg-kb-primary" title="New activity since last visit" data-testid={`guidance-tab-${t.key}-badge`}></span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* CHAT TAB — unified ProjectChat (same as IdeaDetail). Handles
                paste-preview, hover 3-dot menu, replies, reactions, search
                and delete-for-everyone out of the box. */}
            {activeTab === "chat" && (
              <>
                <ChatDescriptionPreview description={guidance.details || guidance.problem_description} />
                <ProjectChat
                  workspaceId={guidance.id}
                  onStartVideoCall={() => startInstantMeet(false)}
                  onStartAudioCall={() => startInstantMeet(true)}
                  statusLine={`${participants.length} participant${participants.length === 1 ? "" : "s"} · ${guidance.status}`}
                />
              </>
            )}

            {/* TASKS TAB — same Kanban as IdeaDetail */}
            {activeTab === "tasks" && (
              <KanbanBoard workspaceId={guidance.id} participants={participants} />
            )}

            {/* PROGRESS LOG TAB — markdown-based, chronological */}
            {activeTab === "progress" && (
              <ProgressLogTab guidanceId={guidance.id} currentUser={user} ownerId={guidance.user_id} mentorUserId={guidance.mentor_user_id} />
            )}

            {/* DOCS TAB — collaborative markdown documents */}
            {activeTab === "docs" && (
              <DocsTab guidanceId={guidance.id} currentUser={user} ownerId={guidance.user_id} wsRef={wsRef} wsConnected={wsConnected} />
            )}

            {/* WHITEBOARD TAB — freehand strokes with save/clear */}
            {activeTab === "whiteboard" && (
              <ExcalidrawWhiteboardTab guidanceId={guidance.id} wsRef={wsRef} wsConnected={wsConnected} currentUser={user} />
            )}

            {/* DRIVE TAB — same DriveTab as IdeaDetail */}
            {activeTab === "drive" && (
              <DriveTab status={googleStatus} onConnect={() => requestExtraScopes(SCOPE_DRIVE)} workspaceTitle={guidance.task_title || guidance.problem_description} workspaceId={guidance.id} />
            )}
          </div>

          {/* ================== RIGHT SIDEBAR ================== */}
          <div className="lg:col-span-3 space-y-3">
            {/* PRIMARY PERSON — always-visible identity for this workspace so
                the viewer immediately knows whose workspace they're in.
                Assignee for task workspaces, client for guidance, team lead
                for teams. Collaborators / co-mentors stay in the collapsible
                card below. */}
            {(() => {
              const primaryId = isTask ? guidance.user_id : (isTeam ? guidance.mentor_user_id : guidance.user_id);
              const p = participants.find((x) => x.user_id === primaryId) || participants[0];
              if (!p) return null;
              const label = isTask ? "ASSIGNEE" : isTeam ? "TEAM LEAD" : "CLIENT";
              return (
                <div className="kb-card p-4 flex items-center gap-3 bg-gradient-to-r from-kb-primary/5 to-transparent" data-testid="workspace-primary-person">
                  <img
                    src={p.picture || `https://ui-avatars.com/api/?background=random&name=${encodeURIComponent(p.name || "?")}`}
                    alt=""
                    className="w-11 h-11 rounded-full border border-slate-200 shrink-0 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="kb-mono text-[9px] uppercase tracking-[0.2em] text-kb-primary">{label}</div>
                    <div className="text-sm font-semibold text-kb-ink truncate">{p.name || p.email}</div>
                    <div className="kb-mono text-[10px] text-kb-ink-muted truncate">{p.role || p.role_in_workspace || ""}{p.affiliation ? ` · ${p.affiliation}` : ""}</div>
                  </div>
                </div>
              );
            })()}
            {/* MENTOR & COLLABORATORS — same ParticipantsCard as IdeaDetail */}
            <details className="kb-card p-5" {...(rightOpen ? { open: true } : {})} data-testid="mentor-collab-card">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{participantsHeader}</div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <div className="mt-3">
                <ParticipantsCard
                  participants={participants}
                  currentUserId={user?.user_id}
                  mentor={mentorObj}
                  mentors={guidance?.co_mentors || []}
                  context={participantsContext}
                  workspaceId={guidance.id}
                  onParticipantsChanged={() => {
                    api.get(`/workspace/${guidance.id}/participants`).then((r) => setParticipants(r.data || [])).catch(() => {});
                  }}
                  canManageMentors={user?.role === "admin" || user?.user_id === guidance?.mentor_user_id || user?.user_id === guidance?.user_id}
                  onAddCoMentor={async (m) => {
                    try { await api.post(`/guidance/${guidance.id}/mentors`, { mentor_id: m.id }); const r = await api.get(`/guidance/${guidance.id}`); setGuidance(r.data); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add mentor"); }
                  }}
                  onRemoveCoMentor={async (mid) => {
                    if (!window.confirm("Remove this co-mentor?")) return;
                    try { await api.delete(`/guidance/${guidance.id}/mentors/${mid}`); const r = await api.get(`/guidance/${guidance.id}`); setGuidance(r.data); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
                  }}
                  onStartCall={(audio) => startInstantMeet(audio)}
                  onMessage={(p) => window.dispatchEvent(new CustomEvent("kb-open-dm", { detail: { user: p } }))}
                />
              </div>
            </details>

            {/* DRIVE FILES — same DrivePreviewSidebar as IdeaDetail */}
            <details className="kb-card p-5" {...(rightOpen ? { open: true } : {})} data-testid="drive-preview-card">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted inline-flex items-center gap-1.5">
                  <FolderOpen className="w-3 h-3" /> DRIVE FILES
                </div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <div className="mt-3">
                <DrivePreviewSidebar hasDrive={googleStatus.has_drive} onConnect={() => requestExtraScopes(SCOPE_DRIVE)} workspaceId={guidance.id} />
              </div>
            </details>

            {/* CODE / GITHUB — for coding-related tasks */}
            <details className="kb-card p-5" {...(rightOpen ? { open: true } : {})} data-testid="task-github-card">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted inline-flex items-center gap-1.5">
                  <Github className="w-3 h-3" /> CODE · GITHUB
                </div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <div className="mt-3 -mx-5 -mb-5">
                <IdeaLinksPanel
                  idea={{ id: guidance.id, links: guidance.links || { github_url: "", drive_folder_url: "", additional_links: [] } }}
                  endpoint={`/guidance/${guidance.id}/links`}
                  onRefresh={reloadGuidance}
                />
              </div>
            </details>

            {/* DELIVERABLES — guidance-specific (mentor-review workflow) */}
            <details className="kb-card p-5" {...(rightOpen ? { open: true } : {})} data-testid="guidance-deliverables">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DELIVERABLES · {deliverables.length}</div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <p className="text-xs text-kb-ink-muted mt-3">
                {isClient ? "Submit your work for the mentor to review." : "Review the client's submitted work."}
              </p>
              {isClient && (
                <form onSubmit={submitDeliverable} className="mt-3 space-y-2 border-t border-slate-200 pt-3" data-testid="deliverable-form">
                  <input className="kb-input text-sm" placeholder="Title (e.g. Draft methodology v1)" value={deliverableForm.title} onChange={(e) => setDeliverableForm({ ...deliverableForm, title: e.target.value })} data-testid="deliverable-title-input" />
                  <textarea rows={2} className="kb-input text-sm resize-none" placeholder="Notes / context (optional)" value={deliverableForm.description} onChange={(e) => setDeliverableForm({ ...deliverableForm, description: e.target.value })} data-testid="deliverable-desc-input" />
                  <div className="flex flex-wrap gap-2 items-center">
                    <button type="button" onClick={() => deliverableFileRef.current?.click()} className="kb-btn-outline text-xs" data-testid="deliverable-attach-btn">
                      <FileUp className="w-3 h-3" /> Attach
                    </button>
                    <input ref={deliverableFileRef} type="file" className="hidden" onChange={(e) => uploadFile(e, "deliverable_attach")} />
                    {deliverableForm.file_ids.length > 0 && (<span className="kb-mono text-[10px] text-emerald-700">{deliverableForm.file_ids.length} attached</span>)}
                    <button type="submit" className="kb-btn-primary text-xs ml-auto" data-testid="deliverable-submit-btn">
                      <CheckCircle2 className="w-3 h-3" /> Submit
                    </button>
                  </div>
                </form>
              )}
              <div className="mt-3 space-y-2">
                {deliverables.length === 0 ? (
                  <div className="text-xs text-kb-ink-muted text-center py-2">No deliverables yet.</div>
                ) : deliverables.map((d) => {
                  const cls = d.status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : d.status === "revision_requested" ? "border-amber-200 bg-amber-50 text-amber-700"
                            : "border-sky-200 bg-sky-50 text-sky-700";
                  return (
                    <div key={d.id} className="border border-slate-200 rounded-sm p-2.5" data-testid={`deliverable-${d.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-kb-ink line-clamp-1">{d.title} <span className="kb-mono text-[9px] text-kb-ink-light">v{d.version}</span></div>
                          <div className="kb-mono text-[9px] text-kb-ink-light">{new Date(d.created_at).toLocaleDateString()} · {d.submitted_by_user_name}</div>
                        </div>
                        <span className={`kb-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${cls}`}>{d.status.replace(/_/g, " ")}</span>
                      </div>
                      {d.description && <div className="text-[11px] text-kb-ink-muted mt-1 line-clamp-2">{d.description}</div>}
                      {d.file_ids?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {d.file_ids.map((fid) => {
                            const f = fileNameById[fid];
                            if (!f) return null;
                            return (
                              <button key={fid} onClick={() => downloadFile(fid, f.name)} className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1 border border-slate-200 px-1.5 py-0.5 rounded-sm hover:border-kb-primary">
                                <Download className="w-2.5 h-2.5" /> {f.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {d.review_note && (<div className="mt-1.5 text-[11px] text-kb-ink-muted italic">&ldquo;{d.review_note}&rdquo;</div>)}
                      {isMentor && d.status === "submitted" && (
                        <div className="mt-1.5 flex gap-1.5">
                          <button onClick={() => reviewDeliverable(d.id, "approved")} className="kb-btn-primary text-[10px]" data-testid={`deliverable-${d.id}-approve`}>
                            <CheckCircle2 className="w-2.5 h-2.5" /> Approve
                          </button>
                          <button onClick={() => reviewDeliverable(d.id, "revision_requested")} className="kb-btn-outline text-[10px]" data-testid={`deliverable-${d.id}-revise`}>
                            <RotateCcw className="w-2.5 h-2.5" /> Revise
                          </button>
                        </div>
                      )}
                      <DeliverableFeedback deliverable={d} guidanceId={guidance.id} />
                    </div>
                  );
                })}
              </div>
            </details>

            {/* SHARED FILES (guidance-specific) — collapsible */}
            <details className="kb-card p-5" data-testid="guidance-files">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SHARED FILES · {files.length}</div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              <label className="mt-3 kb-mono text-[10px] uppercase tracking-wider text-kb-primary cursor-pointer inline-flex items-center gap-1.5" data-testid="file-upload-label">
                <FileUp className="w-3 h-3" /> Upload (max 4MB)
                <input type="file" className="hidden" onChange={(e) => uploadFile(e, "file")} data-testid="file-upload-input" />
              </label>
              <div className="mt-3 space-y-1.5">
                {files.length === 0 ? (
                  <div className="text-xs text-kb-ink-muted text-center py-2">No files yet.</div>
                ) : files.map((f) => (
                  <div key={f.id} className="border border-slate-200 rounded-sm p-2" data-testid={`file-${f.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-kb-ink line-clamp-1 break-all">{f.name}</div>
                        <div className="kb-mono text-[9px] text-kb-ink-light">{formatBytes(f.size_kb)} · v{f.version}</div>
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <button onClick={() => downloadFile(f.id, f.name)} className="p-1 text-kb-ink-muted hover:text-kb-primary" title="Download" data-testid={`file-${f.id}-download`}>
                          <Download className="w-3 h-3" />
                        </button>
                        {(f.uploaded_by_user_id === user?.user_id || isMentor) && (
                          <button onClick={() => deleteFile(f.id)} className="p-1 text-rose-600 hover:text-rose-700" title="Delete" data-testid={`file-${f.id}-delete`}>
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    {f.mime?.startsWith("video/") && (<div className="mt-1 kb-mono text-[9px] text-kb-ink-light inline-flex items-center gap-1"><Play className="w-2.5 h-2.5" /> Video</div>)}
                    {f.mime?.startsWith("image/") && (<div className="mt-1 kb-mono text-[9px] text-kb-ink-light"><LinkIcon className="w-2.5 h-2.5 inline" /> Image</div>)}
                  </div>
                ))}
              </div>
            </details>

            {/* LEGACY MEET LINK (guidance-specific) — collapsible */}
            <details className="kb-card p-5" data-testid="guidance-meet">
              <summary className="cursor-pointer list-none flex items-center justify-between">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MANUAL MEETING LINK</div>
                <ChevronRight className="w-4 h-4 text-kb-ink-muted" />
              </summary>
              {isMentor && !meetEditing && (
                <button onClick={() => setMeetEditing(true)} className="mt-3 kb-mono text-[10px] uppercase tracking-wider text-kb-primary" data-testid="meet-edit-btn">
                  {meetEmbed ? "Edit link" : "Add manual link"}
                </button>
              )}
              {meetEditing ? (
                <div className="mt-3 space-y-2">
                  <input className="kb-input text-sm" placeholder="https://meet.google.com/abc-defg-hij" value={meetForm.meet_url} onChange={(e) => setMeetForm({ ...meetForm, meet_url: e.target.value })} data-testid="meet-url-input" />
                  <input type="datetime-local" className="kb-input text-sm" value={meetForm.scheduled_at} onChange={(e) => setMeetForm({ ...meetForm, scheduled_at: e.target.value })} />
                  <input className="kb-input text-sm" placeholder="Note (agenda)" value={meetForm.note} onChange={(e) => setMeetForm({ ...meetForm, note: e.target.value })} />
                  <div className="flex gap-2">
                    <button onClick={saveMeet} className="kb-btn-primary text-sm" data-testid="meet-save-btn">Save</button>
                    <button onClick={() => setMeetEditing(false)} className="kb-btn-outline text-sm">Cancel</button>
                  </div>
                </div>
              ) : meetEmbed ? (
                <div className="mt-3 space-y-2">
                  <a href={meetEmbed} target="_blank" rel="noreferrer" className="text-kb-primary text-xs font-medium hover:underline truncate inline-flex items-center gap-1" data-testid="meet-join-link">
                    <Video className="w-3 h-3" /> Join meeting <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                  {guidance.meet_scheduled_at && (
                    <div className="kb-mono text-[10px] text-kb-ink-muted inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> {new Date(guidance.meet_scheduled_at).toLocaleString()}
                    </div>
                  )}
                  {guidance.meet_note && <div className="text-[11px] text-kb-ink-muted">&ldquo;{guidance.meet_note}&rdquo;</div>}
                </div>
              ) : (
                <div className="mt-3 text-xs text-kb-ink-muted">Use the Meetings bar inside the workspace for instant Meet links.</div>
              )}
            </details>
          </div>
        </div>
      )}

      {/* CALL PANELS — rendered globally via <PersistentCallHost /> so they
          survive route changes. Nothing to render inline here. */}
    </div>
  );
}

// ============================== Progress Log Tab ==============================
// Chronological, Markdown-rendered progress feed. Any member can post an update;
// the author or workspace owner/admin can retract it. Paginated for scale.
export function ProgressLogTab({ guidanceId, currentUser, ownerId, mentorUserId }) {
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [skip, setSkip] = React.useState(0);
  const bodyRef = React.useRef(null);
  const PAGE = 20;
  const canModerate = ["admin", "super_admin"].includes(currentUser?.role)
    || (currentUser?.secondary_roles || []).some((r) => ["admin", "super_admin"].includes(r))
    || currentUser?.user_id === ownerId
    || currentUser?.user_id === mentorUserId;

  const load = React.useCallback(async (nextSkip = 0, replace = true) => {
    try {
      const r = await api.get(`/guidance/${guidanceId}/progress`, { params: { skip: nextSkip, limit: PAGE } });
      setTotal(r.data?.total || 0);
      setItems((prev) => replace ? (r.data?.items || []) : [...prev, ...(r.data?.items || [])]);
      setSkip(nextSkip + (r.data?.items || []).length);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load progress log"); }
  }, [guidanceId]);

  React.useEffect(() => { load(0, true); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const r = await api.post(`/guidance/${guidanceId}/progress`, { body: text });
      setItems((arr) => [r.data, ...arr]);
      setTotal((t) => t + 1);
      setBody("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't post update"); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm("Retract this progress update? It won't be shown to the team anymore.")) return;
    try {
      await api.delete(`/guidance/${guidanceId}/progress/${p.id}`);
      setItems((arr) => arr.filter((x) => x.id !== p.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  return (
    <div className="kb-card p-5 space-y-5" data-testid="workspace-progress-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PROGRESS LOG</div>
          <h3 className="font-serif text-xl text-kb-ink mt-1">Team updates · <span className="kb-mono text-sm text-kb-ink-muted">{total} entr{total === 1 ? "y" : "ies"}</span></h3>
        </div>
      </div>
      {/* Composer */}
      <form onSubmit={submit} className="space-y-2" data-testid="progress-composer">
        <MarkdownToolbar textareaRef={bodyRef} value={body} onChange={setBody} />
        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder={"Post a Markdown update — # headings, **bold**, checklists, code blocks…"}
          className="kb-input font-mono text-xs w-full resize-y"
          data-testid="progress-composer-input"
        />
        <div className="flex items-center justify-between">
          <span className="kb-mono text-[10px] text-kb-ink-light">Markdown supported · everyone in this workspace can see your update</span>
          <button type="submit" disabled={busy || !body.trim()} className="kb-btn-primary text-xs inline-flex items-center gap-1" data-testid="progress-composer-submit">
            <Send className="w-3 h-3" /> {busy ? "Posting…" : "Post update"}
          </button>
        </div>
      </form>
      {/* Feed */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-slate-300 rounded-sm text-sm text-kb-ink-muted">
            No progress posted yet. Kick off the thread with a Markdown update above.
          </div>
        ) : items.map((p) => {
          const mine = p.user_id === currentUser?.user_id;
          return (
            <div key={p.id} className="border border-slate-200 rounded-sm p-4 bg-white" data-testid={`progress-item-${p.id}`}>
              <div className="flex items-center gap-2 mb-2">
                {p.user_picture ? (
                  <img src={p.user_picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-[10px] text-kb-primary">{(p.user_name || "?")[0]?.toUpperCase()}</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-kb-ink truncate">{p.user_name || p.user_id}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-light">{p.user_role} · {new Date(p.created_at).toLocaleString()}</div>
                </div>
                {(mine || canModerate) && (
                  <button type="button" onClick={() => remove(p)} className="p-1 text-rose-500 hover:text-rose-700" title="Retract" data-testid={`progress-item-${p.id}-delete`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="kb-task-md text-sm text-kb-ink leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.body}</ReactMarkdown>
              </div>
            </div>
          );
        })}
        {items.length < total && (
          <div className="text-center pt-2">
            <button type="button" onClick={() => load(skip, false)} className="kb-btn-outline text-xs" data-testid="progress-load-more">
              Load older ({total - items.length} remaining)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================== Docs Tab ==============================
// Markdown docs per workspace with a compact list on the left and a
// title+body editor on the right. Auto-save on blur; every edit updates
// updated_at + updated_by so members can see who touched it last.
// ============================== Docs Tab ==============================
// Markdown docs per workspace with a compact list on the left and a
// title+body editor on the right. Auto-save on blur; every edit updates
// updated_at + updated_by so members can see who touched it last.
export function DocsTab({ guidanceId, currentUser, ownerId, wsRef, wsConnected }) {
  const [docs, setDocs] = React.useState([]);
  const [activeId, setActiveId] = React.useState(null);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [remoteCursors, setRemoteCursors] = React.useState({}); // user_id -> {name, color, pos, at}
  // Files originally uploaded with the idea / task submission — surfaced
  // here so the assignee can download them from the workspace instead of
  // digging through the admin chat / submission email.
  const [submissionFiles, setSubmissionFiles] = React.useState([]);
  const bodyRef = React.useRef(null);
  const suppressBroadcast = React.useRef(false); // set true when applying remote patch
  const active = docs.find((d) => d.id === activeId) || null;

  React.useEffect(() => {
    api.get(`/workspace/${guidanceId}/submission-attachments`)
      .then((r) => setSubmissionFiles(r.data || []))
      .catch(() => setSubmissionFiles([]));
  }, [guidanceId]);

  const load = React.useCallback(async (preferId) => {
    try {
      const r = await api.get(`/guidance/${guidanceId}/docs`);
      const list = r.data || [];
      setDocs(list);
      if (list.length === 0) { setActiveId(null); return; }
      const keep = preferId && list.find((d) => d.id === preferId);
      const pick = keep || list[0];
      setActiveId(pick.id); setTitle(pick.title || ""); setBody(pick.body || ""); setDirty(false);
    } catch { /* silent */ }
  }, [guidanceId]);
  React.useEffect(() => { load(); }, [load]);

  // ---- Real-time collaboration: listen to workspace WS for doc_* events.
  // We attach a secondary `message` listener so the parent's `onmessage`
  // property is not disturbed.
  React.useEffect(() => {
    const ws = wsRef?.current;
    if (!ws) return;
    const handler = (e) => {
      let payload;
      try { payload = JSON.parse(e.data); } catch { return; }
      const t = payload?.type, d = payload?.data;
      if (!d || d.user_id === currentUser?.user_id) return; // ignore self
      if (t === "doc_edit" && d.doc_id === activeId) {
        suppressBroadcast.current = true;
        setBody(d.body ?? "");
        setDirty(true); // mark dirty so REST save fires on blur / save button
        setTimeout(() => { suppressBroadcast.current = false; }, 0);
      } else if (t === "doc_title" && d.doc_id === activeId) {
        suppressBroadcast.current = true;
        setTitle(d.title ?? "");
        setDirty(true);
        setTimeout(() => { suppressBroadcast.current = false; }, 0);
      } else if (t === "doc_cursor" && d.doc_id === activeId) {
        setRemoteCursors((r) => ({
          ...r,
          [d.user_id]: { name: d.user_name, color: d.user_color, pos: d.pos, at: Date.now() },
        }));
      } else if (t === "doc_edit" || t === "doc_title") {
        // Someone else edited a different doc — refresh the list so the
        // "updated by X" line updates without a full reload.
        setDocs((arr) => arr.map((x) => x.id === d.doc_id
          ? { ...x, updated_by_name: d.user_name, updated_at: new Date().toISOString(),
              ...(t === "doc_title" && d.title !== undefined ? { title: d.title } : {}) }
          : x));
      }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [wsRef, activeId, currentUser?.user_id]);

  // Prune stale cursors (older than 5s) once per second.
  React.useEffect(() => {
    const iv = setInterval(() => {
      setRemoteCursors((r) => {
        const now = Date.now(); let changed = false; const next = {};
        for (const [uid, c] of Object.entries(r)) {
          if (now - (c.at || 0) < 5000) next[uid] = c; else changed = true;
        }
        return changed ? next : r;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const send = (type, data) => {
    const ws = wsRef?.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(JSON.stringify({ type, data })); } catch { /* ignore */ }
  };

  const onBodyChange = (e) => {
    const v = e.target.value;
    setBody(v); setDirty(true);
    if (!suppressBroadcast.current && activeId) send("doc_edit", { doc_id: activeId, body: v });
  };
  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitle(v); setDirty(true);
    if (!suppressBroadcast.current && activeId) send("doc_title", { doc_id: activeId, title: v });
  };
  const onBodyCursor = () => {
    if (!activeId || !bodyRef.current) return;
    send("doc_cursor", { doc_id: activeId, pos: bodyRef.current.selectionStart });
  };

  const openDoc = (d) => {
    if (dirty && !window.confirm("You have unsaved changes. Discard?")) return;
    setActiveId(d.id); setTitle(d.title || ""); setBody(d.body || ""); setDirty(false); setRemoteCursors({});
  };
  const createDoc = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/guidance/${guidanceId}/docs`, { title: "Untitled note", body: "" });
      await load(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create"); }
    finally { setBusy(false); }
  };
  const saveDoc = async () => {
    if (!activeId) return;
    setBusy(true);
    try {
      const r = await api.put(`/guidance/${guidanceId}/docs/${activeId}`, { title, body });
      setDocs((arr) => arr.map((x) => x.id === activeId ? r.data : x));
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const removeDoc = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/guidance/${guidanceId}/docs/${d.id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const canDelete = (d) => !d ? false : (
    d.created_by === currentUser?.user_id ||
    currentUser?.user_id === ownerId ||
    ["admin", "super_admin"].includes(currentUser?.role)
  );

  const cursorList = Object.entries(remoteCursors);
  const [fullscreen, setFullscreen] = React.useState(false);
  const hostRef = React.useRef(null);
  // Sync our React state with the browser's actual fullscreen state — this
  // covers Esc / F11 exits initiated outside our button.
  React.useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === hostRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = React.useCallback(async () => {
    const el = hostRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) await document.exitFullscreen();
      else if (el.requestFullscreen) await el.requestFullscreen();
    } catch { setFullscreen((v) => !v); }
  }, []);

  // Debounced auto-save — fires ~1.8s after the user stops typing.
  React.useEffect(() => {
    if (!activeId || !dirty || busy) return;
    const t = setTimeout(() => { saveDoc(); }, 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, title, dirty, activeId]);

  // Compute pixel positions for remote cursors over the textarea (only in
  // the browser — `textarea-caret` walks the textarea's computed style).
  const [caretPositions, setCaretPositions] = React.useState({});
  React.useEffect(() => {
    const el = bodyRef.current; if (!el) { setCaretPositions({}); return; }
    let cancelled = false;
    (async () => {
      const mod = await import("textarea-caret");
      if (cancelled) return;
      const getCaret = mod.default || mod;
      const next = {};
      for (const [uid, c] of Object.entries(remoteCursors)) {
        const pos = Math.max(0, Math.min(body.length, c.pos || 0));
        try {
          const { top, left, height } = getCaret(el, pos);
          next[uid] = { top, left, height, name: c.name, color: c.color };
        } catch { /* skip */ }
      }
      setCaretPositions(next);
    })();
    return () => { cancelled = true; };
  }, [remoteCursors, body]);

  return (
    <div
      ref={hostRef}
      className={`${fullscreen ? "kb-wb-fs bg-white overflow-auto p-4" : "kb-card overflow-hidden"}`}
      data-testid="workspace-docs-tab"
    >
      {submissionFiles.length > 0 && (
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3" data-testid="workspace-submission-attachments">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-2">SUBMITTED ATTACHMENTS · {submissionFiles.length}</div>
          <div className="flex flex-wrap gap-2">
            {submissionFiles.map((f) => (
              <a
                key={f.id || f.url}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                download={f.name}
                className="inline-flex items-center gap-1.5 text-xs text-kb-ink border border-slate-200 bg-white px-2.5 py-1.5 rounded-sm hover:border-kb-primary hover:text-kb-primary"
                data-testid={`submission-attachment-${f.id}`}
                title={`${f.origin || "submission"}${f.size ? ` · ${Math.round((f.size || 0) / 1024)}KB` : ""}`}
              >
                <FileText className="w-3.5 h-3.5 text-kb-primary" />
                <span className="truncate max-w-[220px]">{f.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-12" style={fullscreen ? { minHeight: "100vh" } : undefined}>
        {/* LEFT — doc list */}
        <div className="col-span-12 md:col-span-4 border-b md:border-b-0 md:border-r border-slate-200 max-h-[65vh] overflow-y-auto">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DOCS ({docs.length})</div>
            <button onClick={createDoc} disabled={busy} className="kb-btn-primary text-[10px] px-2 py-1 inline-flex items-center gap-1" data-testid="docs-new-btn">
              <Plus className="w-3 h-3" /> New
            </button>
          </div>
          {docs.length === 0 ? (
            <div className="p-6 text-xs text-kb-ink-muted text-center">No docs yet. Click <strong>New</strong> to start writing.</div>
          ) : docs.map((d) => {
            const on = d.id === activeId;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => openDoc(d)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-2 ${on ? "bg-kb-primary/5" : ""}`}
                data-testid={`docs-item-${d.id}`}
              >
                <FileText className="w-3.5 h-3.5 text-kb-ink-light mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className={`text-xs font-medium truncate ${on ? "text-kb-primary" : "text-kb-ink"}`}>{d.title || "Untitled"}</div>
                  <div className="kb-mono text-[9px] text-kb-ink-light truncate">{d.updated_by_name} · {new Date(d.updated_at).toLocaleString()}</div>
                </div>
              </button>
            );
          })}
        </div>
        {/* RIGHT — editor */}
        <div className="col-span-12 md:col-span-8 p-4 space-y-3">
          {!active ? (
            <div className="text-center py-16 text-sm text-kb-ink-muted">Pick a doc on the left, or create a new one.</div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <input
                  value={title}
                  onChange={onTitleChange}
                  onBlur={() => dirty && saveDoc()}
                  placeholder="Document title"
                  className="kb-input font-serif text-lg font-medium flex-1"
                  data-testid="docs-title-input"
                />
                {/* Live-presence chips — one per remote editor currently in this doc. */}
                {cursorList.length > 0 && (
                  <div className="flex items-center gap-1" data-testid="docs-presence">
                    {cursorList.map(([uid, c]) => (
                      <span key={uid} className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border" style={{ borderColor: c.color, color: c.color }} title={`${c.name} · editing`}>
                        ● {c.name?.split(" ")[0] || "peer"}
                      </span>
                    ))}
                  </div>
                )}
                <span className={`kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${wsConnected ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-kb-ink-muted border border-slate-200"}`}>
                  {wsConnected ? "Live" : "Offline"}
                </span>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="p-1.5 text-kb-ink-muted hover:text-kb-ink border border-slate-200 rounded-sm"
                  title={fullscreen ? "Exit full-screen (Esc)" : "Full-screen editor"}
                  data-testid="docs-fullscreen-toggle"
                >
                  {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                </button>
                <button type="button" onClick={saveDoc} disabled={busy || !dirty} className="kb-btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="docs-save-btn">
                  <Save className="w-3 h-3" /> {busy ? "Saving…" : dirty ? "Save" : "Saved"}
                </button>
                {canDelete(active) && (
                  <button type="button" onClick={() => removeDoc(active)} className="p-1.5 text-rose-500 hover:text-rose-700" title="Delete" data-testid="docs-delete-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className={`grid grid-cols-1 ${fullscreen ? "lg:grid-cols-2" : "lg:grid-cols-2"} gap-3`}>
                <div className="space-y-2 relative">
                  <MarkdownToolbar textareaRef={bodyRef} value={body} onChange={(v) => {
                    setBody(v); setDirty(true);
                    if (activeId) send("doc_edit", { doc_id: activeId, body: v });
                  }} />
                  <div className="relative">
                    <textarea
                      ref={bodyRef}
                      value={body}
                      onChange={onBodyChange}
                      onSelect={onBodyCursor}
                      onKeyUp={onBodyCursor}
                      onClick={onBodyCursor}
                      onBlur={() => dirty && saveDoc()}
                      rows={fullscreen ? 32 : 20}
                      placeholder="Write in Markdown — # headings, **bold**, - lists, `code`, tables, etc."
                      className="kb-input font-mono text-xs resize-y w-full"
                      style={fullscreen ? { minHeight: "70vh" } : undefined}
                      data-testid="docs-body-input"
                    />
                    {/* Live remote carets — Google-Docs-style caret + name tag. */}
                    {Object.entries(caretPositions).map(([uid, cp]) => (
                      <span
                        key={uid}
                        className="pointer-events-none absolute"
                        style={{ top: cp.top, left: cp.left, height: cp.height, borderLeft: `2px solid ${cp.color}` }}
                      >
                        <span
                          className="absolute -top-4 left-0 kb-mono text-[9px] uppercase tracking-wider text-white px-1 rounded-sm whitespace-nowrap"
                          style={{ background: cp.color }}
                        >
                          {(cp.name || "peer").split(" ")[0]}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
                <div
                  className="kb-task-md text-sm text-kb-ink border border-slate-200 rounded-sm p-4 bg-slate-50 overflow-y-auto"
                  style={fullscreen ? { maxHeight: "78vh" } : { maxHeight: "65vh" }}
                  data-testid="docs-preview"
                >
                  {body ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown> : <div className="text-kb-ink-light italic">Preview will appear here as you type.</div>}
                </div>
              </div>
              <div className="kb-mono text-[10px] text-kb-ink-light">
                Last edited by {active.updated_by_name} · {new Date(active.updated_at).toLocaleString()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================== Whiteboard Tab ==============================
// Now backed by Excalidraw for real usability (proper text edit, resize,
// pen with pressure/tablet support, non-pixelated fullscreen). See
// /app/frontend/src/components/WhiteboardExcalidraw.jsx.
export { WhiteboardTab } from "@/components/WhiteboardExcalidraw";



// ============================== Markdown Toolbar ==============================
// Compact toolbar that inserts / wraps markdown syntax around the current
// selection of a controlled textarea. Reused by both the Progress Log
// composer and the Docs editor.
export function MarkdownToolbar({ textareaRef, value, onChange }) {
  const apply = (kind) => {
    const ta = textareaRef?.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? start;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    // returns [newSel, prefix, suffix, cursorOffset?]
    const map = {
      h1:      () => [sel || "Heading", "# ", "\n"],
      h2:      () => [sel || "Heading", "## ", "\n"],
      bold:    () => [sel || "bold text", "**", "**"],
      italic:  () => [sel || "italic text", "*", "*"],
      code:    () => sel.includes("\n")
                     ? [sel, "```\n", "\n```\n"]
                     : [sel || "code", "`", "`"],
      link:    () => [sel || "link text", "[", "](https://)"],
      list:    () => [(sel || "item").split(/\n/).map((l) => `- ${l}`).join("\n"), "", "\n"],
      check:   () => [(sel || "task").split(/\n/).map((l) => `- [ ] ${l}`).join("\n"), "", "\n"],
      quote:   () => [(sel || "quote").split(/\n/).map((l) => `> ${l}`).join("\n"), "", "\n"],
    };
    const fn = map[kind]; if (!fn) return;
    const [newSel, prefix, suffix] = fn();
    const nextVal = before + prefix + newSel + suffix + after;
    onChange(nextVal);
    // Restore selection just after the inserted content on next tick.
    requestAnimationFrame(() => {
      if (!ta.isConnected) return;
      const pos = (before + prefix + newSel).length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };
  const btn = "p-1.5 rounded-sm border border-slate-200 text-kb-ink-muted hover:bg-slate-100 hover:text-kb-ink transition-colors";
  return (
    <div className="flex items-center flex-wrap gap-1" data-testid="md-toolbar">
      <button type="button" onClick={() => apply("h1")}     title="Heading 1"  className={btn} data-testid="md-h1"><Heading1 className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("h2")}     title="Heading 2"  className={btn} data-testid="md-h2"><Heading2 className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("bold")}   title="Bold"       className={btn} data-testid="md-bold"><Bold className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("italic")} title="Italic"     className={btn} data-testid="md-italic"><Italic className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("code")}   title="Code"       className={btn} data-testid="md-code"><Code className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("link")}   title="Link"       className={btn} data-testid="md-link"><LinkIcon className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("list")}   title="Bullet list" className={btn} data-testid="md-list"><List className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("check")}  title="Checklist"  className={btn} data-testid="md-check"><ListChecks className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => apply("quote")}  title="Quote"      className={btn} data-testid="md-quote"><Quote className="w-3.5 h-3.5" /></button>
    </div>
  );
}


// ============================== Workspace Collaborators Panel ==============================
// Compact side card showing every member of a workspace (owner, mentor,
// participants) with an inline add-by-email/name field and a remove button.
// Works against unified `/workspace/{id}/collaborators` endpoints so the
// same panel drops into both GuidanceWorkspace and IdeaDetail.
export function WorkspaceCollaboratorsPanel({ workspaceId, currentUser, ownerId, mentorUserId }) {
  const [rows, setRows] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [pickUid, setPickUid] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [role, setRole] = React.useState("Collaborator");
  const canManage = ["admin", "super_admin"].includes(currentUser?.role)
    || (currentUser?.secondary_roles || []).some((r) => ["admin", "super_admin"].includes(r))
    || currentUser?.user_id === ownerId
    || currentUser?.user_id === mentorUserId;

  const load = React.useCallback(async () => {
    try {
      const r = await api.get(`/workspace/${workspaceId}/collaborators`);
      setRows(r.data || []);
    } catch { /* silent */ }
  }, [workspaceId]);
  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!canManage || users.length > 0) return;
    // Employees hit /admin/users → 403; fall back to /employee/assignable
    api.get("/admin/users?role=&q=&page=1&page_size=500")
      .then((r) => setUsers((r.data?.items || r.data || []).filter((x) => x.role !== "client")))
      .catch(() => api.get("/employee/assignable")
        .then((r) => setUsers((r.data || []).filter((x) => x.role !== "client")))
        .catch(() => {}));
  }, [canManage]);

  const add = async () => {
    if (!pickUid) { toast.error("Pick a user first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/workspace/${workspaceId}/participants`, {
        user_id: pickUid, role_in_workspace: role,
      });
      if (r.data?.already) toast.info("Already a collaborator");
      else toast.success("Collaborator added");
      setPickUid(""); setSearch("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (r.is_owner) { toast.error("Owner can't be removed"); return; }
    if (!window.confirm(`Remove ${r.name} from this workspace?`)) return;
    try {
      await api.delete(`/workspace/${workspaceId}/participants/${r.user_id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="kb-card p-4 space-y-3" data-testid="workspace-collaborators-panel">
      <div className="flex items-center justify-between">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">COLLABORATORS · {rows.length}</div>
      </div>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.user_id} className={`flex items-center gap-2 px-2.5 py-2 rounded-sm border ${r.is_owner ? "border-emerald-200 bg-emerald-50" : r.is_mentor ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`ws-collab-${r.user_id}`}>
            {r.picture ? (
              <img src={r.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-[10px] text-kb-primary">{(r.name || "?")[0]?.toUpperCase()}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-kb-ink truncate">{r.name}{r.user_id === currentUser?.user_id ? <span className="text-kb-ink-light"> (you)</span> : null}</div>
              <div className="kb-mono text-[9px] text-kb-ink-muted truncate uppercase tracking-wider">{r.role_in_workspace}{r.role ? ` · ${r.role}` : ""}</div>
            </div>
            {canManage && !r.is_owner && (
              <button type="button" onClick={() => remove(r)} className="p-1 text-rose-500 hover:text-rose-700" title="Remove" data-testid={`ws-collab-remove-${r.user_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canManage && (
        <div className="pt-2 border-t border-slate-100 space-y-1.5" data-testid="ws-collab-add-row">
          <input
            list="ws-collab-user-list"
            placeholder="Add a collaborator…"
            className="kb-input text-xs h-7"
            value={search}
            onChange={(e) => {
              const val = e.target.value;
              const match = users.find((u) => `${u.name || u.email} — ${u.email}` === val || u.email === val);
              setSearch(val); setPickUid(match?.user_id || "");
            }}
            data-testid="ws-collab-input"
          />
          <datalist id="ws-collab-user-list">
            {users.slice(0, 200).map((u) => <option key={u.user_id} value={`${u.name || u.email} — ${u.email}`} />)}
          </datalist>
          <div className="flex items-center gap-1.5">
            <input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Role (Collaborator, Reviewer…)"
              className="kb-input text-xs h-7 flex-1"
              data-testid="ws-collab-role"
            />
            <button type="button" onClick={add} disabled={!pickUid || busy} className="kb-btn-primary text-[10px] h-7 px-2 inline-flex items-center gap-1 disabled:opacity-40" data-testid="ws-collab-add-btn">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



/** Small always-available collapsible in the Chat tab so assigners can
 *  re-read the task description while responding to questions without
 *  scrolling up to the workspace title card. */
function ChatDescriptionPreview({ description }) {
  const [open, setOpen] = React.useState(false);
  if (!description) return null;
  return (
    <details
      className="mb-3 border border-slate-200 rounded-sm bg-slate-50"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="chat-description-preview"
    >
      <summary className="cursor-pointer list-none px-3 py-2 flex items-center gap-2 text-xs kb-mono uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary">
        <Info className="w-3.5 h-3.5" />
        <span>Task description</span>
        <ChevronRight className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-90" : ""}`} />
      </summary>
      <div className="px-4 pb-3 pt-1 kb-task-md text-sm text-kb-ink leading-relaxed border-t border-slate-200">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{description}</ReactMarkdown>
      </div>
    </details>
  );
}

/** Assigner ↔ assignee feedback thread rendered under each deliverable.
 *  Reuses the existing `feedback_comments` push endpoint and updates
 *  optimistically so the thread feels snappy inside the deliverables card. */
function DeliverableFeedback({ deliverable, guidanceId }) {
  const [comments, setComments] = React.useState(deliverable.feedback_comments || []);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setComments(deliverable.feedback_comments || []); }, [deliverable.feedback_comments]);
  const submit = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      const r = await import("@/lib/api").then((m) => m.default.post(`/guidance/${guidanceId}/deliverables/${deliverable.id}/comments`, { body }));
      setComments((prev) => [...prev, r.data]);
      setText("");
    } catch { /* noop — toast handled globally */ }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`deliverable-${deliverable.id}-feedback`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary"
      >
        {open ? "Hide" : "Show"} feedback ({comments.length})
      </button>
      {open && (
        <>
          <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto">
            {comments.length === 0 && <div className="text-[10px] text-kb-ink-light italic">No feedback yet.</div>}
            {comments.map((c) => (
              <div key={c.id} className="border border-slate-200 rounded-sm p-1.5 bg-slate-50">
                <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-light flex items-center gap-1.5">
                  <span className="text-kb-ink">{c.author_name || "Someone"}</span>
                  {["admin", "mentor", "tutor", "employee"].includes(c.author_role) && (
                    <span className="text-[8px] px-1 py-0.5 rounded-sm bg-kb-primary/10 text-kb-primary">ASSIGNER</span>
                  )}
                  <span className="text-kb-ink-light">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="text-[11px] text-kb-ink mt-0.5 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
          </div>
          <form onSubmit={submit} className="mt-2 flex gap-1.5">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Reply / give feedback…"
              className="kb-input text-[11px] flex-1"
              data-testid={`deliverable-${deliverable.id}-feedback-input`}
            />
            <button
              type="submit"
              disabled={busy || !text.trim()}
              className="kb-btn-primary text-[10px] px-2"
              data-testid={`deliverable-${deliverable.id}-feedback-send`}
            >Send</button>
          </form>
        </>
      )}
    </div>
  );
}

